import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { App } from 'antd';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '@/hooks/useAppDispatch';

import { clearDrawnPolygon, setDrawnPolygonRing } from '../stores/annotationsSlice';
import {
  getAdaptiveSnapMeters,
  isAnalysisPolygonRingValid,
  isTooCloseToLastVertex,
  isWithinCloseSnapToFirst,
  wouldNewEdgeCrossOpenRing,
} from '../utils/analysisRegionDrawingGeometry';
import { getSarAoiMaxAreaKm2, isOpenRingAreaWithinMaxKm2 } from '../utils/sarAoiGeometry';

export { isAnalysisPolygonRingValid } from '../utils/analysisRegionDrawingGeometry';

/** Giới hạn đỉnh để tránh lag Deck khi vẽ quá dày. */
export const MAP_ANALYSIS_REGION_MAX_VERTICES = 200;

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return target.closest('[contenteditable="true"]') != null;
}

/**
 * Trạng thái vẽ vùng phân tích 2D: điểm nháp cục bộ, polygon hoàn tất trong Redux (đồng bộ Cesium).
 * Phím tắt: Space/Enter hoàn tất, Esc hủy nháp, Backspace/Delete hoàn tác đỉnh (bỏ qua khi focus ô nhập liệu).
 */
export type UseMapAnalysisRegionDrawingOptions = {
  onValidPolygonCommitted?: (ring: [number, number][]) => void;
};

export function useMapAnalysisRegionDrawing(options?: UseMapAnalysisRegionDrawingOptions) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { message } = App.useApp();
  const optionsRef = useRef(options);

  useLayoutEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const [drawingMode, setDrawingMode] = useState(false);
  const [drawingPoints, setDrawingPoints] = useState<[number, number][]>([]);
  const [cursorPos, setCursorPos] = useState<[number, number] | null>(null);
  const [mapZoom, setMapZoom] = useState<number | null>(null);

  const drawingPointsRef = useRef(drawingPoints);

  useLayoutEffect(() => {
    drawingPointsRef.current = drawingPoints;
  }, [drawingPoints]);

  const currentSnapMeters = useMemo(() => {
    if (mapZoom == null || cursorPos == null) {
      // Không dùng fallback mét — tránh vùng snap quá rộng khi zoom chưa đồng bộ.
      return 0;
    }
    return getAdaptiveSnapMeters(mapZoom, cursorPos[1]);
  }, [mapZoom, cursorPos]);

  const previewRingInvalid = useMemo(() => {
    if (!cursorPos || drawingPoints.length < 2) return false;
    const trial = [...drawingPoints, cursorPos];
    if (trial.length < 3) return false;
    if (!isAnalysisPolygonRingValid(trial)) return true;
    const maxKm2 = getSarAoiMaxAreaKm2();
    return !isOpenRingAreaWithinMaxKm2(trial, maxKm2);
  }, [cursorPos, drawingPoints]);

  const snapCloseHighlight = useMemo(
    () =>
      drawingPoints.length >= 3 && cursorPos != null
        ? isWithinCloseSnapToFirst(drawingPoints, cursorPos, currentSnapMeters)
        : false,
    [cursorPos, drawingPoints, currentSnapMeters],
  );

  const resetLocalDraft = useCallback(() => {
    setDrawingPoints([]);
    setCursorPos(null);
  }, []);

  const startDrawing = useCallback(() => {
    setDrawingMode(true);
    dispatch(clearDrawnPolygon());
    resetLocalDraft();
  }, [dispatch, resetLocalDraft]);

  const finishDrawing = useCallback(() => {
    const pts = drawingPointsRef.current;
    if (pts.length >= 3) {
      if (!isAnalysisPolygonRingValid(pts)) {
        message.warning(t('map.analysisRegion.warningSelfIntersect'));
        return;
      }
      const maxKm2 = getSarAoiMaxAreaKm2();
      if (!isOpenRingAreaWithinMaxKm2(pts, maxKm2)) {
        message.error(t('map.sarAoi.areaExceedsMax', { max: maxKm2 }));
        return;
      }
      const ring = [...pts] as [number, number][];
      dispatch(setDrawnPolygonRing(ring));
      optionsRef.current?.onValidPolygonCommitted?.(ring);
    } else {
      dispatch(clearDrawnPolygon());
    }
    setDrawingMode(false);
    resetLocalDraft();
  }, [dispatch, message, resetLocalDraft, t]);

  const cancelDrawingSession = useCallback(() => {
    if (!drawingMode) return;
    setDrawingMode(false);
    resetLocalDraft();
  }, [drawingMode, resetLocalDraft]);

  const clearAll = useCallback(() => {
    setDrawingMode(false);
    dispatch(clearDrawnPolygon());
    resetLocalDraft();
  }, [dispatch, resetLocalDraft]);

  const toggleDrawing = useCallback(() => {
    if (drawingMode) {
      finishDrawing();
    } else {
      startDrawing();
    }
  }, [drawingMode, finishDrawing, startDrawing]);

  const addVertex = useCallback(
    (coord: [number, number]) => {
      setDrawingPoints((prev) => {
        if (prev.length >= MAP_ANALYSIS_REGION_MAX_VERTICES) {
          message.warning(
            t('map.analysisRegion.warningMaxVertices', { max: MAP_ANALYSIS_REGION_MAX_VERTICES }),
          );
          return prev;
        }
        return [...prev, coord];
      });
    },
    [message, t],
  );

  const applyDrawingMapClick = useCallback(
    (coord: [number, number]) => {
      const pts = drawingPointsRef.current;
      if (pts.length >= 3 && isWithinCloseSnapToFirst(pts, coord, currentSnapMeters)) {
        finishDrawing();
        return;
      }
      if (isTooCloseToLastVertex(pts, coord)) {
        return;
      }
      if (pts.length >= 2 && wouldNewEdgeCrossOpenRing(pts, coord)) {
        message.warning(t('map.analysisRegion.warningEdgeCross'));
        return;
      }
      addVertex(coord);
    },
    [addVertex, currentSnapMeters, finishDrawing, message, t],
  );

  const undoLastVertex = useCallback(() => {
    setDrawingPoints((p) => (p.length > 0 ? p.slice(0, -1) : p));
  }, []);

  const setHoverCoordinate = useCallback((coord: [number, number] | null) => {
    setCursorPos(coord);
  }, []);

  useEffect(() => {
    if (!drawingMode) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (isEditableTarget(e.target)) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelDrawingSession();
        return;
      }
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        undoLastVertex();
        return;
      }
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        finishDrawing();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [drawingMode, cancelDrawingSession, finishDrawing, undoLastVertex]);

  return {
    drawingMode,
    drawingPoints,
    cursorPos,
    mapZoom,
    previewRingInvalid,
    snapCloseHighlight,
    startDrawing,
    finishDrawing,
    toggleDrawing,
    cancelDrawingSession,
    clearAll,
    addVertex,
    applyDrawingMapClick,
    undoLastVertex,
    setHoverCoordinate,
    setMapZoom,
  };
}

export type MapAnalysisRegionDrawingApi = ReturnType<typeof useMapAnalysisRegionDrawing>;
